import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { ABILITY_ISSUE, ABILITY_ISSUE_REASON_OF_DELAY, INTENTION_ISSUE, modeOfPayment, strings } from '@constants'
import { Spacings, Text } from '@styles'
import API from '@data/conn/api'
import getUrl from '@data/conn/get/paths'
import { createPaymentReceivable, getDueLedgerReport } from '@data'
import { addRemarkToPaymentReceivables, editPaymentReceivable } from '@data/conn/put/ledger'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, updateDrawerMeta } from '@data/state/action/root'
import { displayAmount, numberToWords, showDateFormat, getCollectionDateRange } from '@utils'
import User from '@data/model/NewModals/User'
import { TOAST_TYPE, useTableReducer, DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { Button, Calendar, Input, MultiSelect, Pill, Select, Table, Textarea } from '@baseV2'

import EditCollection from './EditCollection'
import { InputsWrapper } from '../styles'

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${Spacings.SPACING_3B};
`

const AmountRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${Spacings.SPACING_3B};
	& > div:first-child {
		flex: 1;
	}
`

const WordsText = styled.div`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const Heading = styled.div`
	font-size: ${Text.SMALL};
	font-weight: 600;
	color: ${({ theme }) => theme.colors.text.primary};
`

const PillsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
`

const transformedModeOfPayment = Object.entries(modeOfPayment).map(([key, value]) => ({ key, label: value }))
const intentionTypes = Object.entries(INTENTION_ISSUE).map(([key, value]) => ({ key, label: value }))
const abilityTypes = Object.entries(ABILITY_ISSUE).map(([key, value]) => ({ key, label: value }))
const ability_issue_reason_of_delay = Object.entries(ABILITY_ISSUE_REASON_OF_DELAY).map(([key, value]) => ({ key, label: value }))
// Expected payment date: a few days back, 30 days forward.
const collectionDateRange = getCollectionDateRange()

const getDate = (date) => (+date === 0 ? new Date().getTime() : +date)

const setFormStateData = (data, paymentReceivables) => {
	const _state = {
		expected_payment_date: { label: strings('expected', 'date'), value: new Date().getTime(), required: true, placeholder: strings(['select', 'expected', 'date']) },
		user_details: { label: strings('client'), value: '', required: true, display_key: 'display_name', primary_key: 'organisation_id', placeholder: strings(['select', 'client']) },
		payment_amount: { label: strings('amount'), value: '', required: true, placeholder: strings('enter', 'amount') },
		payment_mode: {
			label: strings('mode_of_payment'),
			data: transformedModeOfPayment,
			value: transformedModeOfPayment?.find((item) => item?.label === modeOfPayment.DIRECT_BANK_TRANSFER),
			primary_key: 'key',
			display_key: 'label',
			required: true,
			placeholder: strings(['select', 'mode_of_payment']),
		},
		message: { label: strings('message'), value: '', required: false, placeholder: strings('add', 'message') },
		issue: { label: strings('issue'), value: '', required: false, placeholder: strings('issue') },
		meeting_remarks: { label: strings('meeting', 'remarks'), value: '', required: false, placeholder: strings('meeting', 'remarks') },
		customer_poc_email: { label: strings('customer', 'email'), value: '', required: false, placeholder: strings(['customer', 'poc', 'email']) },
		customer_poc_name: { label: strings('customer', 'name'), value: '', required: false, placeholder: strings('customer', 'name') },
		customer_poc_phone: { label: strings('customer', 'phone'), value: '', required: false, placeholder: strings('customer', 'phone') },
		meeting_scheduled_at: { label: strings('meeting', 'scheduled'), value: new Date().getTime(), required: false, placeholder: strings(['meeting', 'scheduled', 'at']) },
		remark_message: { label: strings('message'), value: '', required: false, placeholder: strings('message') },
		pdc: { label: 'Can customer give PDCS?', value: '', required: false, placeholder: strings('pdc') },
		principal_details: { label: strings('ask_principal_details'), value: '', required: false, placeholder: strings('details_principal') },
		reason_for_delay: {
			label: strings('reason_for_delay_placeholder'),
			value: '',
			data: ability_issue_reason_of_delay,
			required: false,
			displayKey: 'label',
			primaryKey: 'key',
			placeholder: strings('reason_for_delay'),
		},
		reason: { id: 'reason', label: strings('reason'), value: '', required: false, primaryKey: 'key', displayKey: 'label', placeholder: strings('reason') },
		partial_payment_agreement: { id: 'partial_payment_agreement', label: strings('partial_payment_agreement'), value: '', required: false },
		invoice_list: { id: 'invoice_list', label: strings(['invoice', 'list']), placeholder: strings('Select invoices'), value: [], required: false },
	}
	if (paymentReceivables != null) {
		_state.user_details.value = paymentReceivables?.user_details
		_state.user_details.value['display_name'] = paymentReceivables?.user_details.organisation_name || paymentReceivables?.user_details?.organisation_id
		_state.payment_mode.value = transformedModeOfPayment?.find((item) => item?.key == paymentReceivables?.payment_mode)
		_state.expected_payment_date.value = paymentReceivables?.expected_payment_date
		_state.payment_amount.value = paymentReceivables?.payment_amount
		_state.customer_poc_name.value = paymentReceivables?.user_details?.full_name
		_state.customer_poc_phone.value = paymentReceivables?.user_details?.mobile_number
		_state.invoice_list.value = paymentReceivables?.invoice_list
	}
	if (!data) return _state
	_state.issue.value = data?.issue
	_state.reason.value = intentionTypes.find((item) => item?.key === data?.reason) || abilityTypes.find((item) => item?.key === data?.reason)
	_state.customer_poc_email.value = data?.customer_poc_email
	_state.customer_poc_name.value = data?.customer_poc_name
	_state.customer_poc_phone.value = data?.customer_poc_phone
	_state.payment_amount.value = data?.amount
	_state.meeting_scheduled_at.value = data?.meeting_scheduled_at
	_state.meeting_remarks.value = data?.meeting_remarks
	_state.remark_message.value = data?.message
	_state.pdc.value = data?.qna?.pdc
	_state.principal_details.value = data?.qna?.principal_details
	_state.reason_for_delay.value = ability_issue_reason_of_delay?.find((item) => item?.key == data?.qna?.reason_for_delay) || {}
	_state.partial_payment_agreement.value = data?.qna?.partial_payment_agreement
	return _state
}

const CreateCollection = ({ paymentReceivables = null, data = null, isAddRemark = false, isEdit = false, update, isEditRemark = false }) => {
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const [clientData, setClientData] = useState([])
	const [formState, setFormState] = useState(setFormStateData(data, paymentReceivables))
	const [collection, setCollection] = useState({})
	const [invoiceList, setInvoiceList] = useState({})
	const [balance, setBalance] = useState(0)
	const [state, tableDispatch, actions] = useTableReducer()
	const globalDispatch = useDispatch()

	useEffect(() => {
		globalDispatch(
			updateDrawerMeta({
				label: isEdit ? strings('edit', 'remark') : isAddRemark ? strings('add', 'remark') : strings('create', 'collection'),
			})
		)
		API.get(getUrl.getAllClients)
			.then((response) => {
				response.data?.map((item) => {
					item['display_name'] = item.organisation_name === '' || item.organisation_name == null ? item.organisation_id : item.organisation_name
					return item
				})
				setClientData(response.data)
			})
			.catch((err) => console.log(err))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (formState.user_details?.value?.organisation_id) {
			tableDispatch(actions.fetchTableDataProgress())
			getDueLedgerReport(formState?.user_details?.value?.organisation_id)
				.then((res) => {
					setBalance(res?.balance)
					tableDispatch(actions.fetchTableDataSuccess(res?.payment_receivables, res?.payment_receivables.length))
					res?.ledger_report?.map((item) => {
						item['display_name'] = `${item.invoice_number} \xa0\xa0\xa0\xa0\xa0\xa0\xa0 (${showDateFormat(new Date(item.transaction_date))}) \xa0\xa0\xa0\xa0\xa0\xa0\xa0 ${displayAmount(item.invoice_amount)}`
						return item
					})
					setInvoiceList(res?.ledger_report)
				})
				.catch((err) => {
					console.log(err)
					tableDispatch(actions.fetchTableDataFailure(err))
				})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formState.user_details])

	const updateState = (key, updates) => setFormState((s) => ({ ...s, [key]: { ...s[key], ...updates } }))

	const onChange = (key) => (value) => {
		const tempCollection = Object.assign({}, collection)
		if (key === 'invoice_list') {
			const tempRegionSet = new Set()
			const tempInvoiceList = []
			value.forEach((e) => {
				tempInvoiceList.push(`${e.invoice_number}`)
				if (e?.region != null) tempRegionSet.add(e?.region)
			})
			tempCollection[key] = tempInvoiceList
			tempCollection['region'] = Array.from(tempRegionSet).join(', ')
		} else if (key === 'payment_mode' || key === 'reason') {
			tempCollection[key] = value.key
		} else {
			tempCollection[key] = value
		}
		setCollection(tempCollection)
		updateState(key, { value })
	}

	const isCreatebuttonDisabled =
		formState.payment_amount.value == null ||
		formState.payment_amount.value === '' ||
		formState.user_details.value == null ||
		formState.user_details.value === '' ||
		formState.payment_mode.value == null ||
		formState.payment_mode.value === '' ||
		formState.expected_payment_date == null

	const formStateToRemark = () => ({
		created_at: data?.created_at ? data?.created_at : Date.now(),
		amount: formState.payment_amount?.value,
		expected_payment_date: formState?.expected_payment_date?.value,
		customer_poc_email: formState.customer_poc_email?.value,
		customer_poc_name: formState.customer_poc_name?.value,
		customer_poc_phone: formState.customer_poc_phone?.value,
		issue: formState.issue?.value,
		meeting_remarks: formState.meeting_remarks?.value,
		meeting_scheduled_at: formState.meeting_scheduled_at?.value,
		message: formState.remark_message?.value,
		user_details: formState.user_details?.value,
		qna: {
			pdc: formState.pdc?.value,
			principal_details: formState.principal_details?.value,
			reason_for_delay: formState.reason_for_delay?.value?.key,
			partial_payment_agreement: formState.partial_payment_agreement?.value,
		},
		reason: formState.reason?.value?.key,
		payment_mode: formState.payment_mode?.value?.key,
	})

	const submitHandler = () => {
		globalDispatch(showLoader(true))
		collection['manager'] = [new User(user).getUserDetails()]
		collection['expected_payment_date'] = formState.expected_payment_date?.value
		if (!('payment_mode' in collection)) collection['payment_mode'] = formState.payment_mode?.value?.key
		createPaymentReceivable(collection)
			.then(() => globalDispatch(showToast(true, strings('msg_collection_created_success'), { type: TOAST_TYPE.SUCCESS })))
			.catch((err) => {
				console.log(err)
				let err_msg = strings('msg_collection_created_failure')
				if (err?.client_error_code === 'E_002' && err?.message) err_msg = err.message
				globalDispatch(showToast(true, err_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				globalDispatch(toggleDrawer(false))
				globalDispatch(showLoader(false))
				update()
			})
	}

	const addRemarkHandler = () => {
		if (paymentReceivables == null) return
		globalDispatch(showLoader(true))
		const remark = formStateToRemark()
		const paymentReceivablesId = paymentReceivables?.receivable_id
		addRemarkToPaymentReceivables({ ...remark, type: 'message' }, paymentReceivablesId)
			.then(() => globalDispatch(showToast(true, strings('msg_collection_remark_added_success'), { type: TOAST_TYPE.SUCCESS })))
			.catch((err) => {
				console.log(err)
				globalDispatch(showToast(true, strings('msg_collection_remark_added_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				globalDispatch(showLoader(false))
				globalDispatch(toggleDrawer(false))
				update()
			})
	}

	const editCollectionHandler = () => {
		globalDispatch(showLoader(true))
		const remark = formStateToRemark()
		const paymentReceivablesId = paymentReceivables?.receivable_id
		editPaymentReceivable(paymentReceivablesId, remark)
			.then(() => globalDispatch(showToast(true, strings('msg_receivable_edited_success'), { type: TOAST_TYPE.SUCCESS })))
			.catch((err) => {
				console.log(err)
				globalDispatch(showToast(true, strings('msg_receivable_edited_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				globalDispatch(toggleDrawer(false))
				update()
				globalDispatch(showLoader(false))
			})
	}

	const tableColumns = useMemo(
		() => [
			{ label: strings('expected', 'date'), key: (d) => showDateFormat(new Date(d.expected_payment_date)), sortable: false },
			{ label: strings(['amount']), key: (d) => (d?.payment_amount ? displayAmount(d?.payment_amount) : '-'), sortable: false },
			{ label: strings('message'), key: (d) => (d?.message ? d?.message : '-'), sortable: false },
		],
		[]
	)

	const isAddRemarkDisabled =
		formState.issue.value == null ||
		formState.issue.value === '' ||
		formState.reason.value == null ||
		formState.reason.value === '' ||
		(formState.reason.value?.key?.toLowerCase() === ABILITY_ISSUE.OTHER.toLowerCase() && formState.remark_message?.value === '') ||
		Object.keys(formState.reason.value).length === 0

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Select
						primaryKey={formState.user_details?.primary_key}
						displayKey={formState.user_details?.display_key}
						data={clientData}
						label={formState.user_details?.label}
						disabled={isEdit || isAddRemark}
						value={formState.user_details?.value}
						placeholder={formState.user_details?.placeholder}
						onChange={onChange('user_details')}
					/>
					<Row>
						<Calendar
							label={formState.expected_payment_date?.label}
							value={getDate(formState.expected_payment_date?.value)}
							placeholder={formState.expected_payment_date?.placeholder}
							onChange={onChange('expected_payment_date')}
							minDate={collectionDateRange.minDate}
							maxDate={collectionDateRange.maxDate}
						/>
						<Select
							primaryKey={formState.payment_mode?.primary_key}
							displayKey={formState.payment_mode?.display_key}
							data={formState.payment_mode?.data}
							label={formState.payment_mode?.label}
							value={formState.payment_mode?.value}
							placeholder={formState.payment_mode?.placeholder}
							onChange={onChange('payment_mode')}
						/>
					</Row>
					<AmountRow>
						<Input
							type='number'
							label={formState.payment_amount?.label}
							value={formState.payment_amount?.value}
							placeholder={formState.payment_amount?.placeholder}
							onChange={(e) => onChange('payment_amount')(e.target.value)}
						/>
						<Pill>{`${strings('total', 'due')}: ${displayAmount(balance)}`}</Pill>
					</AmountRow>
					{formState.payment_amount?.value != 0 && formState.payment_amount?.value != null && (
						<WordsText>{numberToWords(formState.payment_amount?.value)}</WordsText>
					)}
					{!isEdit && !isAddRemark && (
						<Textarea
							id={formState.message.id}
							label={strings('message')}
							rows={3}
							placeholder={formState.message.placeholder}
							value={formState.message?.value}
							onChange={(e) => onChange('message')(e.target.value)}
						/>
					)}
					{!isEdit && !isAddRemark && (
						<MultiSelect
							id={formState.invoice_list.id}
							label={formState.invoice_list.label}
							displayKey='display_name'
							primaryKey='display_name'
							placeholder={formState.invoice_list.placeholder}
							data={invoiceList}
							value={formState.invoice_list.value}
							onChange={onChange('invoice_list')}
						/>
					)}
					{(isEdit || isAddRemark) && (
						<>
							<Heading>{strings(['invoice', 'numbers', ' : '])}</Heading>
							<PillsRow>
								{formState.invoice_list?.value?.map((item, index) => (
									<Pill key={`invoice-${index}`}>{item}</Pill>
								))}
							</PillsRow>
						</>
					)}
					{!isEdit && !isAddRemark && balance !== null && state?.data?.length > 0 && (
						<>
							<Heading>{strings('previous', 'collections')}</Heading>
							<Table data={state.data} columns={tableColumns} loading={state.loading} rowClickable={false} hideFooter />
						</>
					)}
					{(isEdit || isAddRemark) && <EditCollection isEdit={isEdit} isAddRemark={isAddRemark} onChange={onChange} state={formState} />}
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				{isEdit ? (
					<Button color='primary' disabled={!isEditRemark || isAddRemarkDisabled} onClick={editCollectionHandler}>
						{strings('edit', 'remark')}
					</Button>
				) : isAddRemark ? (
					<Button color='primary' disabled={isAddRemarkDisabled} onClick={addRemarkHandler}>
						{strings('add', 'remark')}
					</Button>
				) : (
					<Button color='primary' disabled={isCreatebuttonDisabled} onClick={submitHandler}>
						{strings('create', 'collection')}
					</Button>
				)}
			</DrawerFooterWrapper>
		</>
	)
}

export default CreateCollection
